import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getJSON, postJSON } from '@/lib/api'
import {
  itemPath,
  queuePath,
  type QueueResponse,
  type SortColumn,
  type UnmatchedItem,
} from './types'

export function useUnmatchedItems() {
  const [search, setSearch] = useState('')
  const [connections, setConnections] = useState<ReadonlySet<string>>(() => {
    const id = new URLSearchParams(window.location.search).get('connection_id')
    return new Set(id ? [id] : [])
  })
  const [reasons, setReasons] = useState<ReadonlySet<string>>(new Set())
  const [statuses, setStatuses] = useState<ReadonlySet<string>>(new Set(['open']))
  const [sort, setSort] = useState<SortColumn>('waiting_rows')
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc')
  const params = new URLSearchParams({ sort, direction })
  if (search) params.set('q', search)
  connections.forEach((value) => params.append('connection_id[]', value))
  reasons.forEach((value) => params.append('reason[]', value))
  const statusValues = statuses.size ? statuses : new Set(['open', 'ignored', 'linked'])
  statusValues.forEach((value) => params.append('status[]', value))
  const rows = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['unmatched-items', params.toString()],
    queryFn: () => getJSON<QueueResponse>(`${queuePath}?${params}`),
  })
  const sortable = (column: SortColumn) => ({
    active: sort === column,
    direction: direction === 'asc' ? ('ascending' as const) : ('descending' as const),
    onToggle: () => {
      setDirection(nextDirection(sort, column, direction))
      setSort(column)
    },
  })
  return {
    search,
    setSearch,
    connections,
    setConnections,
    reasons,
    setReasons,
    statuses,
    setStatuses,
    rows,
    sortable,
  }
}
function nextDirection(current: SortColumn, next: SortColumn, direction: 'asc' | 'desc') {
  if (current !== next) return next === 'name' ? 'asc' : 'desc'
  return direction === 'asc' ? 'desc' : 'asc'
}
type Command = { row: UnmatchedItem; action: string; contactId?: number } | { ids: number[] }
export function useUnmatchedAction() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (command: Command) => {
      if ('ids' in command) return postJSON(`${queuePath}/bulk_ignore`, { item_ids: command.ids })
      const body = command.contactId ? { contact_id: command.contactId } : {}
      return postJSON(`${itemPath(command.row)}/${command.action}`, body)
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['unmatched-items'] }),
        client.invalidateQueries({ queryKey: ['connections'] }),
      ])
    },
  })
}
export function useQueueSelection(rows: UnmatchedItem[]) {
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const selectable = rows.filter((row) => row.status === 'open')
  const ids = selectable.filter((row) => selected.has(row.id)).map((row) => row.id)
  const all = selectable.length > 0 && ids.length === selectable.length
  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  return {
    ids,
    all,
    toggle,
    clear: () => setSelected(new Set()),
    toggleAll: () => setSelected(new Set(all ? [] : selectable.map((row) => row.id))),
  }
}
